/**
 * TUVI - Gestión de Usuarios
 * CRUD completo para administración de usuarios del sistema
 */

import React, { useState, useEffect } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { MENU_STRUCTURE } from '../Layout/Sidebar';
import './Usuarios.css';

const Usuarios = () => {
  const { success, error: showError } = useNotification();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('crear'); // 'crear' | 'editar'
  const [usuarioActual, setUsuarioActual] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [permisos, setPermisos] = useState({});

  // Estado del formulario
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    nombre_completo: '',
    password: '',
    confirmar_password: '',
    rol: 'general',
    activo: true
  });

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const data = await client.query(`{ usuarios { id username email nombreCompleto rol activo ultimoLogin } }`);
      setUsuarios((data.usuarios || []).map(u => ({ ...u, nombre_completo: u.nombreCompleto, ultimo_login: u.ultimoLogin })));
    } catch (err) {
      showError('Error al cargar usuarios: ' + err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar usuarios
  const usuariosFiltrados = usuarios.filter(usuario => {
    // Filtro por búsqueda
    if (busqueda) {
      const searchLower = busqueda.toLowerCase();
      const matchBusqueda = (
        usuario.username?.toLowerCase().includes(searchLower) ||
        usuario.nombre_completo?.toLowerCase().includes(searchLower) ||
        usuario.email?.toLowerCase().includes(searchLower)
      );
      if (!matchBusqueda) return false;
    }

    return true;
  });

  // Cargar permisos de un usuario
  const cargarPermisos = async (usuarioId) => {
    try {
      const data = await client.query(`query($uid: Int!) { usuarioPermisos(usuario_id: $uid) }`, { uid: usuarioId });
      setPermisos(data.usuarioPermisos || {});
    } catch (err) {
      console.error('Error cargando permisos:', err);
    }
  };

  // Guardar permisos de un usuario
  const guardarPermisos = async (usuarioId) => {
    try {
      await client.mutate(`mutation($uid: Int!, $permisos: JSONObject!) { actualizarPermisos(usuario_id: $uid, permisos: $permisos) { success } }`, { uid: usuarioId, permisos });
    } catch (err) {
      console.error('Error guardando permisos:', err);
    }
  };

  // Toggle permiso individual
  const togglePermiso = (modulo, submodulo) => {
    const key = submodulo ? `${modulo}.${submodulo}` : modulo;
    setPermisos(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Toggle todo un módulo
  const toggleModulo = (modulo) => {
    const submenus = MENU_STRUCTURE.find(m => m.id === modulo)?.submenu || [];
    const allActive = submenus.every(sub => permisos[`${modulo}.${sub.id}`]);
    const newPermisos = { ...permisos };
    submenus.forEach(sub => {
      newPermisos[`${modulo}.${sub.id}`] = !allActive;
    });
    setPermisos(newPermisos);
  };

  // Seleccionar todos los permisos
  const seleccionarTodos = () => {
    const newPermisos = {};
    MENU_STRUCTURE.forEach(mod => {
      if (mod.submenu && mod.submenu.length > 0) {
        mod.submenu.forEach(sub => {
          newPermisos[`${mod.id}.${sub.id}`] = true;
        });
      } else {
        newPermisos[mod.id] = true;
      }
    });
    setPermisos(newPermisos);
  };

  // Quitar todos los permisos
  const quitarTodos = () => {
    setPermisos({});
  };

  // Abrir modal para crear
  const abrirModalCrear = () => {
    setModalMode('crear');
    setUsuarioActual(null);
    setPermisos({});
    setFormData({
      username: '',
      email: '',
      nombre_completo: '',
      password: '',
      confirmar_password: '',
      rol: 'general',
      activo: true
    });
    setModalOpen(true);
  };

  // Abrir modal para editar
  const abrirModalEditar = async (usuario) => {
    setModalMode('editar');
    setUsuarioActual(usuario);
    setFormData({
      username: usuario.username || '',
      email: usuario.email || '',
      nombre_completo: usuario.nombre_completo || '',
      password: '',
      confirmar_password: '',
      rol: usuario.rol || 'general',
      activo: usuario.activo
    });
    await cargarPermisos(usuario.id);
    setModalOpen(true);
  };

  // Cerrar modal
  const cerrarModal = () => {
    setModalOpen(false);
    setUsuarioActual(null);
    setPermisos({});
    setFormData({
      username: '',
      email: '',
      nombre_completo: '',
      password: '',
      confirmar_password: '',
      rol: 'general',
      activo: true
    });
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Guardar usuario (crear o actualizar)
  const guardarUsuario = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!formData.username.trim()) {
      showError('El nombre de usuario es requerido');
      return;
    }

    if (!formData.nombre_completo.trim()) {
      showError('El nombre completo es requerido');
      return;
    }

    if (modalMode === 'crear' && !formData.password) {
      showError('La contraseña es requerida');
      return;
    }

    if (formData.password && formData.password !== formData.confirmar_password) {
      showError('Las contraseñas no coinciden');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      showError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      let data;
      if (modalMode === 'crear') {
        const gqlR = await client.mutate(`mutation($input: CrearUsuarioInput!) { crearUsuario(input: $input) { success message usuario_id } }`, {
          input: { username: formData.username.trim(), password: formData.password, nombre_completo: formData.nombre_completo.trim(), rol: formData.rol, email: formData.email.trim() || null, activo: !!formData.activo }
        });
        data = gqlR.crearUsuario;
      } else {
        const gqlR = await client.mutate(`mutation($id: Int!, $input: ActualizarUsuarioInput!) { actualizarUsuario(id: $id, input: $input) { success message } }`, {
          id: usuarioActual.id,
          input: { username: formData.username.trim(), nombre_completo: formData.nombre_completo.trim(), rol: formData.rol, email: formData.email.trim() || null, password: formData.password || null, activo: !!formData.activo }
        });
        data = gqlR.actualizarUsuario;
      }

      if (data.success) {
        const userId = modalMode === 'crear' ? data.usuario_id : usuarioActual.id;
        if (userId) {
          await guardarPermisos(userId);
        }
        success(modalMode === 'crear' ? 'Usuario creado correctamente' : 'Usuario actualizado correctamente');
        cerrarModal();
        cargarUsuarios();
      } else {
        throw new Error(data.message || 'Error al guardar usuario');
      }
    } catch (err) {
      showError(err.message);
      console.error('Error:', err);
    }
  };

  // Eliminar usuario
  const eliminarUsuario = async (usuario) => {
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarUsuario(id: $id) { success message } }`, { id: usuario.id });

      if (gqlR.eliminarUsuario.success) {
        success('Usuario eliminado correctamente');
        setConfirmDelete(null);
        cargarUsuarios();
      } else {
        throw new Error(gqlR.eliminarUsuario.message || 'Error al eliminar usuario');
      }
    } catch (err) {
      showError(err.message);
      console.error('Error:', err);
    }
  };

  // Formatear fecha
  const formatDate = (fecha) => {
    if (!fecha) return 'Nunca';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Badge de estado
  const EstadoBadge = ({ activo }) => {
    return (
      <span className={`estado-badge ${activo ? 'activo' : 'inactivo'}`}>
        {activo ? 'Activo' : 'Inactivo'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="usuarios-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="usuarios-container">
      {/* Header */}
      <div className="usuarios-header">
        <div className="header-left">
          <h2>Usuarios del Sistema</h2>
          <p className="subtitle">
            {usuariosFiltrados.length} de {usuarios.length} usuarios
          </p>
        </div>
        <button className="btn-nuevo" onClick={abrirModalCrear}>
          + Nuevo Usuario
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros-section">
        <div className="busqueda-box">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Buscar por usuario, nombre o email..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="search-input"
          />
        </div>

      </div>


      {/* Tabla de usuarios */}
      {usuariosFiltrados.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">👥</span>
          <h3>No se encontraron usuarios</h3>
          <p>
            {busqueda
              ? 'Intenta ajustar los filtros de búsqueda'
              : 'Comienza creando el primer usuario'}
          </p>
        </div>
      ) : (
        <div className="tabla-wrapper">
          <table className="usuarios-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre Completo</th>
                <th>Email</th>
                <th>Estado</th>
                <th>Último Acceso</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.map((usuario) => (
                <tr key={usuario.id}>
                  <td>
                    <span className="username">{usuario.username}</span>
                  </td>
                  <td>
                    <span className="nombre">{usuario.nombre_completo || '-'}</span>
                  </td>
                  <td>
                    <span className="email">{usuario.email || '-'}</span>
                  </td>
                  <td>
                    <EstadoBadge activo={usuario.activo} />
                  </td>
                  <td>
                    <span className="fecha">{formatDate(usuario.ultimo_login)}</span>
                  </td>
                  <td>
                    <div className="acciones">
                      <button
                        className="btn-accion editar"
                        onClick={() => abrirModalEditar(usuario)}
                        title="Editar usuario"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      {usuario.username !== 'admin' && (
                        <button
                          className="btn-accion eliminar"
                          onClick={() => setConfirmDelete(usuario)}
                          title="Eliminar usuario"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Crear/Editar Usuario */}
      {modalOpen && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'crear' ? 'Nuevo Usuario' : 'Editar Usuario'}</h3>
              <button className="btn-cerrar" onClick={cerrarModal}>×</button>
            </div>

            <form onSubmit={guardarUsuario}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Usuario *</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="Nombre de usuario"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="correo@ejemplo.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Nombre Completo *</label>
                  <input
                    type="text"
                    name="nombre_completo"
                    value={formData.nombre_completo}
                    onChange={handleInputChange}
                    placeholder="Nombre y apellidos"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{modalMode === 'crear' ? 'Contraseña *' : 'Nueva Contraseña'}</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder={modalMode === 'crear' ? 'Mínimo 6 caracteres' : 'Dejar vacío para no cambiar'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirmar Contraseña</label>
                    <input
                      type="password"
                      name="confirmar_password"
                      value={formData.confirmar_password}
                      onChange={handleInputChange}
                      placeholder="Repetir contraseña"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="activo"
                        checked={formData.activo}
                        onChange={handleInputChange}
                      />
                      <span>Usuario Activo</span>
                    </label>
                  </div>
                </div>

                {/* Permisos por módulo */}
                <div className="permisos-section">
                  <div className="permisos-header">
                    <h4>Permisos de Acceso</h4>
                    <div className="permisos-actions">
                      <button type="button" className="btn-permiso-all" onClick={seleccionarTodos}>Todos</button>
                      <button type="button" className="btn-permiso-none" onClick={quitarTodos}>Ninguno</button>
                    </div>
                  </div>
                  <div className="permisos-grid">
                    {MENU_STRUCTURE.map(modulo => {
                      const submenus = modulo.submenu || [];
                      // Módulos sin submenu (clickeables directo): permiso único = modulo.id
                      if (submenus.length === 0) {
                        const activo = !!permisos[modulo.id];
                        return (
                          <div key={modulo.id} className="permiso-modulo">
                            <label className={`permiso-modulo-header ${activo ? 'all-active' : ''}`}>
                              <input
                                type="checkbox"
                                checked={activo}
                                onChange={() => togglePermiso(modulo.id, null)}
                              />
                              <span className="permiso-modulo-name">{modulo.name}</span>
                              <span className="permiso-count">{activo ? '1/1' : '0/1'}</span>
                            </label>
                          </div>
                        );
                      }
                      const activeCount = submenus.filter(sub => permisos[`${modulo.id}.${sub.id}`]).length;
                      const allActive = activeCount === submenus.length;
                      const someActive = activeCount > 0 && !allActive;

                      return (
                        <div key={modulo.id} className="permiso-modulo">
                          <label className={`permiso-modulo-header ${allActive ? 'all-active' : someActive ? 'some-active' : ''}`}>
                            <input
                              type="checkbox"
                              checked={allActive}
                              ref={el => { if (el) el.indeterminate = someActive; }}
                              onChange={() => toggleModulo(modulo.id)}
                            />
                            <span className="permiso-modulo-name">{modulo.name}</span>
                            <span className="permiso-count">{activeCount}/{submenus.length}</span>
                          </label>
                          <div className="permiso-submodulos">
                            {submenus.map(sub => (
                              <label key={sub.id} className={`permiso-sub ${permisos[`${modulo.id}.${sub.id}`] ? 'active' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={!!permisos[`${modulo.id}.${sub.id}`]}
                                  onChange={() => togglePermiso(modulo.id, sub.id)}
                                />
                                <span>{sub.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancelar" onClick={cerrarModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-guardar">
                  {modalMode === 'crear' ? 'Crear Usuario' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminación */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header warning">
              <h3>Confirmar Eliminación</h3>
              <button className="btn-cerrar" onClick={() => setConfirmDelete(null)}>×</button>
            </div>

            <div className="modal-body">
              <div className="confirm-message">
                <span className="warning-icon">⚠️</span>
                <p>
                  ¿Estás seguro de que deseas eliminar al usuario <strong>{confirmDelete.nombre_completo}</strong> ({confirmDelete.username})?
                </p>
                <p className="warning-text">Esta acción no se puede deshacer.</p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancelar" onClick={() => setConfirmDelete(null)}>
                Cancelar
              </button>
              <button className="btn-eliminar" onClick={() => eliminarUsuario(confirmDelete)}>
                Eliminar Usuario
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
